import React from 'react';
import Button from '../common/Button'; 
import { 
  BarChart3, 
  Users, 
  Car, 
  Settings, 
  CreditCard,
  Bell,
  Search,
  LayoutDashboard,
  Apple,
  Play,
  QrCode,
  Smartphone
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const AgencyCTA = () => {
  const { t } = useLanguage();

  return (
    <section className="bg-slate-900 text-white py-20 mt-12 overflow-hidden relative">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600 rounded-full blur-[120px] opacity-10"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-500 rounded-full blur-[120px] opacity-10"></div>

      <div className="container mx-auto px-4 relative z-10 flex flex-col lg:flex-row items-center gap-16">
        
        {/* Left Content (Text) */}
        <div className="flex-1 lg:max-w-xl">
          <span className="inline-block px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-wider mb-6 border border-orange-500/30">
            Pour les Professionnels
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Gérez votre flotte comme un <span className="text-blue-400">Pro</span>.
          </h2>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Rejoignez Carvy Pro et accédez à un tableau de bord complet. Digitalisez vos contrats, suivez vos revenus en temps réel et recevez des réservations 24h/24.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg shadow-lg shadow-blue-900/50 border-none">
              Devenir Partenaire
            </Button>
            <Button variant="outline" className="border-gray-600 text-gray-300 hover:text-white hover:bg-white/10 hover:border-white px-8 py-4 text-lg">
              Voir la démo
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
            <div>
              <div className="text-3xl font-bold text-white mb-1">50+</div>
              <div className="text-sm text-gray-400">Agences Actives</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">240+</div>
              <div className="text-sm text-gray-400">Véhicules</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">24/7</div>
              <div className="text-sm text-gray-400">Support Dédié</div>
            </div>
          </div>
        </div>

        {/* Right Content (The Dashboard Mockup) */}
        <div className="flex-1 w-full relative">
          {/* Main Dashboard Window */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-700 relative z-20 transform rotate-1 hover:rotate-0 transition duration-500 ease-out">
            
            {/* Header (Fake Browser) */}
            <div className="bg-slate-800 p-3 flex items-center gap-4 border-b border-slate-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="bg-slate-900 text-gray-400 text-xs px-3 py-1 rounded-md flex-1 text-center font-mono">
                admin.carvy.tech/dashboard
              </div>
            </div>

            <div className="flex h-[400px] text-slate-800">
              {/* Sidebar (Based on your screenshot) */}
              <div className="w-48 bg-slate-700 flex flex-col text-gray-300 p-3 hidden sm:flex">
                <div className="font-bold text-white text-xl mb-6 px-2 flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center text-xs">C</div>
                  Carvy
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-3 px-3 py-2 bg-blue-600 text-white rounded-md cursor-pointer">
                    <LayoutDashboard size={16} /> <span className="text-sm font-medium">Dashboard</span>
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-600 rounded-md cursor-pointer transition">
                    <Car size={16} /> <span className="text-sm">Vehicles</span>
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-600 rounded-md cursor-pointer transition">
                    <CreditCard size={16} /> <span className="text-sm">Bookings</span>
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-600 rounded-md cursor-pointer transition">
                    <Users size={16} /> <span className="text-sm">Customers</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-600">
                    <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-600 rounded-md cursor-pointer transition">
                      <Settings size={16} /> <span className="text-sm">Settings</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 bg-gray-50 p-6 overflow-hidden relative">
                {/* Top Bar */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-xl text-gray-800">Vue d'ensemble</h3>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-white rounded-full shadow-sm flex items-center justify-center text-gray-400">
                      <Search size={14} />
                    </div>
                    <div className="w-8 h-8 bg-white rounded-full shadow-sm flex items-center justify-center text-gray-400 relative">
                      <Bell size={14} />
                      <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full border border-white"></div>
                    </div>
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                      NB
                    </div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="text-xs text-gray-400 font-medium uppercase mb-1">Revenus (MAD)</div>
                    <div className="text-2xl font-bold text-gray-800">14,250</div>
                    <div className="text-xs text-green-500 font-medium mt-1 flex items-center gap-1">
                      ↑ 12% <span className="text-gray-400 font-normal">vs hier</span>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="text-xs text-gray-400 font-medium uppercase mb-1">Réservations</div>
                    <div className="text-2xl font-bold text-gray-800">28</div>
                    <div className="text-xs text-blue-500 font-medium mt-1 flex items-center gap-1">
                      4 <span className="text-gray-400 font-normal">en attente</span>
                    </div>
                  </div>
                </div>

                {/* Chart Mockup */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-32 flex items-end justify-between px-6 pb-2 gap-2">
                   {[40, 65, 30, 80, 55, 90, 70].map((h, i) => (
                     <div key={i} className="w-full bg-blue-50 rounded-t-sm relative group">
                        <div 
                          className="absolute bottom-0 w-full bg-blue-500 rounded-t-sm transition-all duration-500 hover:bg-blue-600" 
                          style={{ height: `${h}%` }}
                        ></div>
                        <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] py-1 px-2 rounded transition">
                          {h * 10}
                        </div>
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>

          {/* Floating Elements (Visual Polish) */}
          <div className="absolute -right-8 -bottom-8 bg-white p-4 rounded-xl shadow-xl z-30 animate-bounce-slow hidden md:block border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                <CreditCard size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-800">Paiement Reçu</div>
                <div className="text-xs text-gray-500">+ 1,400.00 MAD</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* App Download Section - Compact */}
      <div className="container mx-auto px-4 relative z-10 mt-20 pt-16 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-10">
          
          {/* 1. Phone Mockup (Compact) */}
          <div className="relative">
            {/* Phone Frame - Compact */}
            <div className="w-40 h-[280px] bg-slate-800 rounded-[2rem] p-1.5 shadow-2xl shadow-black/50 border border-slate-700">
              <div className="w-full h-full bg-gradient-to-b from-[#27489E] to-[#1e3a7d] rounded-[1.5rem] overflow-hidden relative flex flex-col items-center justify-center">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-slate-800 rounded-b-xl"></div>
                
                {/* App Icon - Style like the provided image */}
                <div className="w-16 h-16 bg-[#27489E] rounded-2xl shadow-lg flex items-center justify-center mb-3 border-2 border-white/20">
                  <span className="text-white text-2xl font-black italic">
                    Carvy
                  </span>
                </div>
                
                <p className="text-white font-bold text-sm">Carvy</p>
                <p className="text-white/60 text-[10px]">Location de voiture</p>
                
                {/* Mini features */}
                <div className="mt-4 space-y-1.5 w-full px-4">
                  {['GPS', 'Réservation', 'Paiement'].map((item, i) => (
                    <div key={i} className="bg-white/10 rounded-lg px-2 py-1 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-[#F5A623] rounded-full"></div>
                      <span className="text-white text-[9px]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating badge - Animated */}
            <div className="absolute -right-3 top-8 bg-white px-2 py-1 rounded-full shadow-lg flex items-center gap-1 text-xs animate-pulse">
              <span className="text-yellow-500 animate-[ping_2s_ease-in-out_infinite]">⭐</span>
              <span className="font-bold text-gray-800">4.8</span>
            </div>
          </div>

          {/* 2. App Info */}
          <div className="text-center md:text-left">
            <div className="flex items-center gap-3 justify-center md:justify-start mb-4">
              {/* App Icon like provided image */}
              <div className="w-12 h-12 bg-[#27489E] rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-lg font-black italic">Cv</span>
              </div>
              <div>
                <h3 className="text-white text-lg font-bold">Carvy App</h3>
                <div className="flex items-center gap-1 text-gray-400 text-xs">
                  10K+ {t('appDownload.downloads')}
                </div>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm mb-5 max-w-xs">
              {t('appDownload.description')}
            </p>

            {/* Store Buttons */}
            <div className="flex gap-3 justify-center md:justify-start">
              <a href="#" className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white px-3 py-2 rounded-xl hover:bg-white/20 transition">
                <Apple className="w-5 h-5" />
                <div className="text-left">
                  <div className="text-[8px] text-gray-400 uppercase leading-tight">{t('appDownload.downloadOn')}</div>
                  <div className="text-xs font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a href="#" className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white px-3 py-2 rounded-xl hover:bg-white/20 transition">
                <Play className="w-5 h-5 fill-current" />
                <div className="text-left">
                  <div className="text-[8px] text-gray-400 uppercase leading-tight">{t('appDownload.getItOn')}</div>
                  <div className="text-xs font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div>
          </div>

          {/* 3. Spacer */}
          <div className="hidden md:block w-16"></div>

          {/* 4. QR Code */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-2xl shadow-xl">
              <div className="w-32 h-32 bg-gray-100 rounded-xl flex items-center justify-center">
                {/* Placeholder for QR Code */}
                <QrCode className="w-24 h-24 text-gray-400" />
              </div>
            </div>
            <div className="text-center mt-3">
              <p className="text-white font-medium text-sm">{t('appDownload.scanQR')}</p>
              <p className="text-gray-500 text-xs">iOS & Android</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgencyCTA;